import * as tus from "tus-js-client";
import axios from "axios";
import { API } from "./api";

/**
 * TUS Cloudflare Stream uploader — resumable, chunked, direct-to-CF.
 *
 * Flow:
 *   1. Ask our backend for a one-time CF Stream TUS URL
 *      (POST /admin/stream/tus/create) — backend calls CF with our
 *      account token and returns { uploadURL, uid }.
 *   2. Use tus-js-client to PATCH 50 MB chunks straight to CF.
 *   3. On success, poll our backend /admin/stream/{uid}/status until
 *      encoding completes and we get back { hls_url, thumbnail_url }.
 *
 * Advantages over the /admin/stream/migrate server-proxy path:
 *   - No 500 MB Nginx cap to fight (CF chunks bypass wisewit.ai entirely).
 *   - Uploads survive intermittent drops (TUS resumes from last chunk).
 *   - Faster overall for >200 MB clips.
 *
 * The server-proxied path is still the default for smaller files because
 * it sidesteps corporate-network TLS-inspection issues (browser only talks
 * to wisewit.ai). Callers should pick per-file: >=THRESHOLD → TUS.
 */

export const TUS_THRESHOLD_BYTES = 200 * 1024 * 1024; // 200 MB

/**
 * @param {File} file
 * @param {string} token - X-Admin-Token
 * @param {(progress: {pct: number, state: string}) => void} onProgress
 * @returns {Promise<{ hls_url: string, thumbnail_url: string, uid: string }>}
 */
export async function uploadToCloudflareStreamTUS(file, token, onProgress) {
  onProgress?.({ pct: 0, state: "requesting" });
  const { data: created } = await axios.post(
    `${API}/admin/stream/tus/create`,
    {
      filename: file.name || "upload.mp4",
      filesize: file.size,
      filetype: file.type || "video/mp4",
    },
    { headers: { "X-Admin-Token": token } },
  );
  const { uploadURL, uid } = created;
  if (!uploadURL || !uid) throw new Error("Backend did not return a TUS upload URL.");

  await new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      uploadUrl: uploadURL,        // one-time URL from CF — no auth header needed
      chunkSize: 50 * 1024 * 1024, // 50 MB, CF-recommended
      retryDelays: [0, 1000, 3000, 5000, 10000],
      metadata: {
        filename: file.name || "upload.mp4",
        filetype: file.type || "video/mp4",
      },
      onError: (err) => reject(err),
      onProgress: (bytesUploaded, bytesTotal) => {
        const pct = Math.round((bytesUploaded / bytesTotal) * 100);
        onProgress?.({ pct, state: "uploading" });
      },
      onSuccess: () => resolve(),
    });
    upload.start();
  });

  // Poll for encoding readiness
  onProgress?.({ pct: 100, state: "encoding" });
  for (let i = 0; i < 90; i++) {
    await new Promise((r) => setTimeout(r, 2000));
    try {
      const { data: st } = await axios.get(`${API}/admin/stream/${uid}/status`, {
        headers: { "X-Admin-Token": token },
      });
      if (st.ready) return { hls_url: st.hls_url, thumbnail_url: st.thumbnail_url, uid };
      if (st.state === "error") throw new Error("Cloudflare reported encoding error.");
    } catch (e) {
      if (e?.response?.status === 404) throw e;
      // transient — keep polling
    }
  }
  throw new Error("Encoding timed out after 3 min. Check Cloudflare dashboard.");
}
